import { GAME_MODES, type GameMode, modeLabel } from '../chart.js';
import { parseCirclesVariantSlug, parseVariantSlug } from '../gameTypes.js';

/**
 * SEO source-of-truth: site constants + pure meta formatters.
 *
 * This module is deliberately LIGHTWEIGHT — beyond the {@link GameMode}
 * union it pulls in only the small runtime helpers it needs from `chart.ts`
 * ({@link GAME_MODES}, {@link modeLabel}) and no chart data, builtin maps,
 * or server code. That keeps it safe to import from both the server (head
 * injection + sitemap) and the React client (per-route head + SPA route
 * resolution) without dragging the whole chart graph into the browser
 * bundle.
 *
 * The per-route formatters are pure `(args) → RouteMeta`, and
 * {@link resolveClientRouteMeta} maps a path → meta for every non-song
 * route, so the catalogue, head-injection and client layers can all be
 * unit-tested without a request or a DB.
 */

export const SITE_URL = 'https://beatheaven.io';
export const SITE_NAME = 'BeatHeaven';

/**
 * Resolved head metadata for a single route. `canonical` is always an
 * absolute URL under {@link SITE_URL}; `noindex` drives both the
 * `robots` meta tag (Stage 2) and exclusion from the sitemap.
 */
export interface RouteMeta {
  path: string;
  title: string;
  description: string;
  canonical: string;
  noindex: boolean;
}

interface ModeSeo {
  /**
   * Genre-led home `<title>` (kept ≤ ~60 chars incl. brand). Leads with the
   * Tier-2 "clone gap" keyword players actually search for (osu online,
   * piano tiles, guitar/clone hero, geometry dash unblocked) and folds in
   * the "online"/"unblocked" qualifier, rather than our invented mode
   * label, which has no search volume on its own.
   */
  homeTitle: string;
  /**
   * Mid-sentence genre clause woven into the mode home description after the
   * mode label — keeps each mode's copy unique and keyword-aware.
   */
  genreLead: string;
  /**
   * Short, keyword-aware `-style` mechanic phrase (e.g. `osu-style`,
   * `guitar-hero / clone-hero-style`) reused in browse / tutorial / song
   * descriptions — the descriptive "X-style tracks" long-tail.
   */
  mechanic: string;
}

/**
 * Per-mode SEO copy. Keyed by {@link GameMode} so the type checker forces an
 * entry for every mode. Keywords come from the Tier-1/Tier-2 targets in
 * `.cursor/rules/seo-overview.mdc` — baked in where they read naturally, not
 * stuffed. Competitor marks are framed for fair use: `-inspired` in the
 * identity-heavy title/lead, `-style` in the keyword-dense `mechanic` (browse /
 * tutorial / song meta — descriptive "X-style tracks" comparison) — never a
 * bare "play <mark>" claim, and always backed by the affiliation disclaimer.
 * Guitar leans hardest on `clone hero` (the biggest unclaimed gap).
 */
const MODE_SEO: Record<GameMode, ModeSeo> = {
  pulse: {
    homeTitle: 'One-Button Rhythm Game — Free & Online | BeatHeaven',
    genreLead: 'a one-button, tap-to-the-beat rhythm game',
    mechanic: 'one-button flip-pad',
  },
  circles: {
    homeTitle: 'osu-Inspired Rhythm Game — Free & Unblocked | BeatHeaven',
    genreLead: 'an osu-inspired click-the-circles rhythm game',
    mechanic: 'osu-style',
  },
  piano: {
    homeTitle: 'Piano Tiles-Inspired Game — Free & Unblocked | BeatHeaven',
    genreLead: 'a Piano Tiles-inspired falling-tile rhythm game',
    mechanic: 'piano-tiles-style',
  },
  guitar: {
    homeTitle: 'Guitar Hero & Clone Hero-Inspired Game — Unblocked | BeatHeaven',
    genreLead: 'a Guitar Hero / Clone Hero-inspired fretboard game',
    mechanic: 'guitar-hero / clone-hero-style',
  },
  waves: {
    homeTitle: 'Geometry-Dash-Inspired Wave Game — Unblocked | BeatHeaven',
    genreLead: 'a Geometry-Dash-inspired wave flight game',
    mechanic: 'Geometry-Dash-style',
  },
  jump: {
    homeTitle: 'Geometry-Dash-Inspired Game — Free & Unblocked | BeatHeaven',
    genreLead: 'a Geometry-Dash-inspired one-button cube runner',
    mechanic: 'Geometry-Dash-style',
  },
};

/**
 * Build an absolute canonical URL for a site-relative path. Strips a
 * trailing slash (except the root) so `/pulse` and `/pulse/` share one
 * canonical, and guarantees a leading slash.
 */
export function buildCanonical(path: string): string {
  if (path === '' || path === '/') {
    return `${SITE_URL}/`;
  }
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  const trimmed = withLeadingSlash.endsWith('/')
    ? withLeadingSlash.slice(0, -1)
    : withLeadingSlash;
  return `${SITE_URL}${trimmed}`;
}

/**
 * Trim a description to ~`max` chars on a word boundary, appending an
 * ellipsis. Song descriptions can run long (up to 500 chars) so song-page
 * meta clamps them rather than letting search engines truncate mid-word.
 */
function clampDescription(text: string, max = 160): string {
  if (text.length <= max) {
    return text;
  }
  const slice = text.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(' ');
  const trimmed = lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${trimmed.trimEnd()}…`;
}

/** Home / mode-picker meta. Leads on the Tier-1 genre long-tail. */
export function homeMeta(): RouteMeta {
  return {
    path: '/',
    title: 'BeatHeaven — Free Online Rhythm Games, Unblocked',
    description:
      'Play free browser rhythm games inspired by osu, Piano Tiles, Guitar ' +
      'Hero / Clone Hero and Geometry Dash. NCS music, custom songs, ' +
      'unblocked at school.',
    canonical: buildCanonical('/'),
    noindex: false,
  };
}

/** Per-mode landing page meta (`/${mode}`). Targets the Tier-2 clone gap. */
export function modeHomeMeta(mode: GameMode, modeLabel: string): RouteMeta {
  const seo = MODE_SEO[mode];
  return {
    path: `/${mode}`,
    title: seo.homeTitle,
    description:
      `Play ${modeLabel} free online — ${seo.genreLead} set to NCS tracks. No ` +
      `download, unblocked at school, with custom songs at ${SITE_NAME}.`,
    canonical: buildCanonical(`/${mode}`),
    noindex: false,
  };
}

/** Community browse page meta (`/${mode}/browse`). */
export function modeBrowseMeta(mode: GameMode, modeLabel: string): RouteMeta {
  const { mechanic } = MODE_SEO[mode];
  return {
    path: `/${mode}/browse`,
    title: `Browse ${modeLabel} Maps — Custom Songs | ${SITE_NAME}`,
    description:
      `Browse community-made ${modeLabel} maps and custom songs at ${SITE_NAME}. ` +
      `Free ${mechanic} rhythm charts to play online, unblocked, no download.`,
    canonical: buildCanonical(`/${mode}/browse`),
    noindex: false,
  };
}

/** Tutorial page meta (`/${mode}/tutorial`). */
export function modeTutorialMeta(mode: GameMode, modeLabel: string): RouteMeta {
  const { mechanic } = MODE_SEO[mode];
  return {
    path: `/${mode}/tutorial`,
    title: `How to Play ${modeLabel} — Tutorial | ${SITE_NAME}`,
    description:
      `Learn how to play ${modeLabel} at ${SITE_NAME} — a quick ${mechanic} ` +
      `tutorial. Free online rhythm game, unblocked, no download.`,
    canonical: buildCanonical(`/${mode}/tutorial`),
    noindex: false,
  };
}

/**
 * Variant-stripped base slug for a song URL. Density variants
 * (`invincible-hard`, `invincible-extreme`, and Circles' extra `-insane`) are
 * near-duplicates of the base song page, so they canonicalise to it. Circles
 * has its own parser for the fourth variant; every other mode shares the
 * normal/hard/extreme scheme. Keeps client (play-page head) and server
 * (catalogue) agreeing on one canonical per song.
 */
export function baseSongSlug(mode: GameMode, slug: string): string {
  return mode === 'circles'
    ? parseCirclesVariantSlug(slug).baseSlug
    : parseVariantSlug(slug).baseSlug;
}

export interface SongPageMetaInput {
  mode: GameMode;
  modeLabel: string;
  /** Slug as it appears in the URL (may be a variant slug — canonicalised). */
  slug: string;
  songTitle: string;
  songDescription: string;
  /** Perceived/display BPM (callers pass `chartDisplayBpm`, not raw chart bpm). */
  bpm: number;
}

/**
 * Song play-page meta (`/${mode}/play/${slug}`). The title carries the song
 * name + mode label; the description weaves in the song's own blurb, the mode
 * mechanic and BPM. `canonical` always points at the bare baseSlug page so
 * near-duplicate `-hard`/`-extreme`/`-insane` variant URLs consolidate to one
 * song page — identically on the server (catalogue) and the client (play-page
 * head), which pass the song's base title via `stripVariantSuffix`.
 */
export function songPageMeta(input: SongPageMetaInput): RouteMeta {
  const { mode, modeLabel, slug, songTitle, songDescription, bpm } = input;
  const path = `/${mode}/play/${slug}`;
  const canonicalPath = `/${mode}/play/${baseSongSlug(mode, slug)}`;
  const mechanic = MODE_SEO[mode].mechanic;
  const description = clampDescription(
    `Play "${songTitle}" in ${modeLabel} at ${SITE_NAME} — ${mechanic} rhythm ` +
      `at ${bpm} BPM. ${songDescription}`,
  );
  return {
    path,
    title: `${songTitle} — ${modeLabel} | ${SITE_NAME}`,
    description,
    canonical: buildCanonical(canonicalPath),
    noindex: false,
  };
}

/**
 * Routes that must carry `noindex` (and are kept out of the sitemap):
 * the personal profile, the calibration utility, the personalised seasons
 * board, every admin page, and every map-editor (`/build`) route. Mirrors
 * the `robots.txt` disallow list.
 */
export function isNoindexPath(path: string): boolean {
  if (path === '/profile' || path === '/calibrate' || path === '/seasons') {
    return true;
  }
  if (path === '/admin' || path.startsWith('/admin/')) {
    return true;
  }
  // Segment match, not substring: `/pulse/build` is the editor (noindex), but
  // a song/UGC slug like `/pulse/play/build-up` must stay indexable.
  if (path.split('/').includes('build')) {
    return true;
  }
  return false;
}

/**
 * Normalise a route/request path for matching: drop the query string and
 * hash, strip a trailing slash (except the bare root), and guarantee a
 * leading slash. Shared by {@link resolveClientRouteMeta} and the server
 * catalogue so both treat `/pulse`, `/pulse/` and `/pulse?x=1` alike.
 */
export function normalisePath(path: string): string {
  let p = path.split('?')[0].split('#')[0];
  if (p.length > 1 && p.endsWith('/')) {
    p = p.slice(0, -1);
  }
  if (!p.startsWith('/')) {
    p = `/${p}`;
  }
  return p;
}

/** Type guard: is this first path segment one of the known game modes? */
export function isGameMode(value: string): value is GameMode {
  return (GAME_MODES as ReadonlyArray<string>).includes(value);
}

/**
 * Meta for a route we deliberately keep out of the index (profile,
 * calibrate, admin, every `/build` editor). Title/description stay generic
 * — these pages carry `noindex` so the copy never surfaces in search.
 */
function noindexMeta(path: string): RouteMeta {
  return {
    path,
    title: SITE_NAME,
    description: `A free browser rhythm game by ${SITE_NAME}.`,
    canonical: buildCanonical(path),
    noindex: true,
  };
}

/**
 * Resolve a client-side route to its head meta WITHOUT touching chart data
 * — safe to call from the React bundle on every SPA navigation. Covers the
 * home picker, each mode home / tutorial / browse page, and the noindex
 * utility routes. Returns `null` for song play pages
 * (`/${mode}/play/${slug}`) — those are owned by the play pages, which read
 * the loaded chart's real title — and for unknown paths.
 *
 * The server catalogue (`seoCatalogue.ts`) delegates these same branches
 * here and only adds the song lookup (which needs the builtin maps) and the
 * browse-capability gate on top.
 */
export function resolveClientRouteMeta(path: string): RouteMeta | null {
  const p = normalisePath(path);
  if (p === '/') {
    return homeMeta();
  }
  if (isNoindexPath(p)) {
    return noindexMeta(p);
  }
  if (p === SONGS_HUB_PATH) {
    return songsHubMeta();
  }
  const segments = p.split('/').filter(Boolean);
  const [maybeMode, sub] = segments;
  // `/songs/:slug` detail pages own their own head (the title needs the song
  // record), so they fall through to `null` here just like song play pages —
  // `songs` is not a game mode, so the guard below returns null for them.
  if (!isGameMode(maybeMode)) {
    return null;
  }
  const label = modeLabel(maybeMode);
  if (segments.length === 1) {
    return modeHomeMeta(maybeMode, label);
  }
  if (segments.length === 2 && sub === 'tutorial') {
    return modeTutorialMeta(maybeMode, label);
  }
  if (segments.length === 2 && sub === 'browse') {
    return modeBrowseMeta(maybeMode, label);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tier-3 content hub (`/songs`, `/songs/:slug`).
//
// Top-of-funnel referral pages targeting `best NCS songs (for gaming)` and
// `songs like [song]`. These formatters stay chart-data-free — the per-song
// resolver in `seoCatalogue.ts` and the `/songs/:slug` page itself feed the
// real song facts in (mirroring how song play pages own their own meta).
// ---------------------------------------------------------------------------

/** Path of the song index hub. */
export const SONGS_HUB_PATH = '/songs';

/** Song index hub metadata, covering NCS and other labels. */
export function songsHubMeta(): RouteMeta {
  return {
    path: SONGS_HUB_PATH,
    title: `Songs to Play — NCS & More | ${SITE_NAME}`,
    description: clampDescription(
      `The songs behind every ${SITE_NAME} mode — charted into six ` +
        'browser rhythm games inspired by osu, Piano Tiles, Guitar Hero and ' +
        'Geometry Dash. Best songs for gaming, no download.',
    ),
    canonical: buildCanonical(SONGS_HUB_PATH),
    noindex: false,
  };
}

export interface SongHubDetailMetaInput {
  /** Bare song slug (e.g. `nekozilla`). */
  slug: string;
  /** Song title. */
  title: string;
  /** Performing artist. */
  artist: string;
  /** Perceived tempo in BPM. */
  displayBpm: number;
}

/**
 * Per-song hub page meta (`/songs/:slug`). Carries the song + artist and leans
 * on the Tier-3 `songs like [song]` long-tail. Self-canonical — these are
 * curated content pages, distinct from the per-mode song play pages.
 */
export function songHubDetailMeta(input: SongHubDetailMetaInput): RouteMeta {
  const { slug, title, artist, displayBpm } = input;
  const path = `${SONGS_HUB_PATH}/${slug}`;
  return {
    path,
    title: `${title} — Rhythm Game | ${SITE_NAME}`,
    description: clampDescription(
      `Play "${title}" by ${artist} free at ${SITE_NAME} — this track ` +
        `with playable modes and its official release. ${displayBpm} BPM. Plus songs ` +
        'like it.',
    ),
    canonical: buildCanonical(path),
    noindex: false,
  };
}
